import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import {
  ActionIcon,
  Group,
  LoadingOverlay,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { useDisclosure, useMounted } from "@mantine/hooks";
import { IconPencil, IconTrash } from "@tabler/icons-react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { EditTreatmentPlanModal } from "@pages/Patients/GeneralConsultation/components/EditTreatmentPlanModal";

import { CustomPagination } from "@components/CustomPagination";

import { formatDateToString } from "@utils/dateValidation";
import { truncateWithEllipsis } from "@utils/truncateText";

import { QP } from "@constants/index";

import { useDeleteTreatmentPlan } from "@hooks/mutations/consultation/useDeleteTreatmentPlan";
import { useGetClinicLabs } from "@hooks/queries/clinic/useGetClinicLabs";
import { useGetTreatmentPlans } from "@hooks/queries/consultation/useGetTreatmentPlans";
import { useGetUsers } from "@hooks/queries/user/useGetUsers";
import { useSyncTableQueryParams } from "@hooks/useSyncTableQueryParams";

import { TreatmentPlan } from "@customTypes/consultation";

import { GeneralTabProps } from "../GeneralTab";
import { StyledGeneralTabTable } from "../GeneralTab/GeneralTab.styled";


const columnHelper = createColumnHelper<Partial<TreatmentPlan>>();

export const TreatmentPlanTab = ({ setIsRefreshing }: GeneralTabProps) => {
  const { t, i18n } = useTranslation();

  const { patientId } = useParams();

  const { debounced, tablePagination, setTablePagination } =
    useSyncTableQueryParams({});

  const [tableData, setTableData] = useState<Partial<TreatmentPlan>[] | []>([]);

  const [isMounted, setIsMounted] = useState(true);
  const mounted = useMounted();
  const [currentTreatmentPlan, setCurrentTreatmentPlan] =
    useState<Partial<TreatmentPlan> | null>(null);
  const [
    editTreatmentPlanModalOpened,
    { open: openEditTreatmentPlanModal, close: closeEditTreatmentPlanModal },
  ] = useDisclosure(false);
  // const [
  //   addTreatmentPlanModalOpened,
  //   { open: openAddTreatmentPlanModal, close: closeAddTreatmentPlanModal },
  // ] = useDisclosure(false);

  const {
    mutate: deleteTreatmentPlan,
    isLoading: isDeleteTreatmentPlanLoading,
  } = useDeleteTreatmentPlan();
  const { data, isError, isSuccess, isLoading, fetchStatus, isRefetching } =
    useGetTreatmentPlans({
      patientId: patientId as string,
      pagination: {
        page: tablePagination.page,
        pageSize: tablePagination.pageSize,
      },
    });
  const { data: clinicLabsData } = useGetClinicLabs();
  const { data: supportStaffData, isLoading: isSupportStaffDataLoading } =
    useGetUsers({ permission: "treatments.edit" });
  const supportStaffComboboxItems = useMemo(() => {
    return supportStaffData?.map(item => ({
      label: item.fullName,
      value: item.id,
    }));
  }, [supportStaffData]);

  const labColumns = useMemo(
    () => [
      columnHelper.accessor("treatment", {
        header: t("treatment"),
        cell: info => truncateWithEllipsis(info.cell.getValue()?.label, 20),
      }),

      columnHelper.accessor("quantity", {
        header: t("quantity"),
      }),
      columnHelper.accessor("supportStaff.label", {
        header: t("support-staff"),
        cell: info => truncateWithEllipsis(info.cell.getValue(), 15),
      }),
      columnHelper.accessor("date", {
        header: t("date"),
        cell: info => {
          return (
            <Stack gap={5}>
              <Text fz={14}>
                {formatDateToString(info.cell.getValue() as Date)}
              </Text>
              <Text c="dimmed" fz={13}>
                {(info.cell.getValue() as Date).toLocaleTimeString(
                  i18n.language,
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                  }
                )}
              </Text>
            </Stack>
          );
        },
      }),
      columnHelper.display({
        id: "action",
        header: t("action"),
        cell: info => {
          return (
            <Group gap={3} justify="end">
              <Tooltip label={"Edit treatment"} withArrow>
                <PermissionGate permission="treatments.edit"><ActionIcon
                  onClick={() => {
                    setCurrentTreatmentPlan(info.row.original);
                    openEditTreatmentPlanModal();
                  }}
                  variant="outline"
                  disabled={isDeleteTreatmentPlanLoading}>
                  <IconPencil size={16} stroke={1.5} />
                </ActionIcon></PermissionGate>
              </Tooltip>
              <Tooltip label={"Delete treatment"} withArrow>
                <PermissionGate permission="treatments.delete"><ActionIcon
                  loading={isDeleteTreatmentPlanLoading}
                  variant="outline"
                  color="red"
                  onClick={() => {
                    deleteTreatmentPlan({
                      treatmentId: info.row.original.id ?? "",
                      patientId: patientId as string,
                    });
                  }}>
                  <IconTrash size={16} stroke={1.5} />
                </ActionIcon></PermissionGate>
              </Tooltip>
            </Group>
          );
        },
      }),
    ],
    [tableData, clinicLabsData, i18n.language, isDeleteTreatmentPlanLoading]
  );

  const table = useReactTable({
    data: tableData,
    columns: labColumns,

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),

    debugTable: false,
  });

  useEffect(() => {
    if (data) {
      setTableData(data.newData);
      setTablePagination({ ...data.pagination });
    } else {
      setTableData([]);
    }
  }, [data]);

  useEffect(() => {
    if (setIsRefreshing) setIsRefreshing(isRefetching);
    if (mounted && isRefetching) return;
    if (mounted && !isRefetching) {
      setIsMounted(false);
    }
  }, [isRefetching, mounted]);

  return (
    <>
      <LoadingOverlay
        visible={isRefetching && !!data?.newData?.length && !isMounted}
        zIndex={1000}
        overlayProps={{ blur: 0.1, opacity: 0.6 }}
        loaderProps={{ display: "none" }}
      />
      <StyledGeneralTabTable>
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <EditTreatmentPlanModal
          opened={editTreatmentPlanModalOpened}
          close={closeEditTreatmentPlanModal}
          supportStaffData={supportStaffComboboxItems ?? []}
          treatmentPlan={currentTreatmentPlan}
        />

        {isSuccess && !data?.newData?.length && debounced ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={labColumns.length}>
                <p>
                  {t("empty-results-for", {
                    searchFilter: `"${debounced}"`,
                  })}
                </p>
              </td>
            </tr>
          </tbody>
        ) : null}

        {isSuccess && !data?.newData?.length && !debounced ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={labColumns.length}>
                <p>{t("empty-labs")}</p>
              </td>
            </tr>
          </tbody>
        ) : null}

        {isLoading && fetchStatus === "fetching" && !tableData.length ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={labColumns.length}>
                <p>{t("loading")}</p>
              </td>
            </tr>
          </tbody>
        ) : null}

        {isError && !data?.newData?.length ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={labColumns.length}>
                <p>{t("encountered-error")}</p>
              </td>
            </tr>
          </tbody>
        ) : null}

        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr key={row.id}>
              {row.getVisibleCells().map(cell => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </StyledGeneralTabTable>

      {(isLoading && fetchStatus === "fetching") || !data ? null : (
        <CustomPagination
          table={table}
          tablePagination={tablePagination}
          otherParams={{
            ...(debounced && { [QP.SEARCH]: debounced }),
            [QP.TAB]: "treatment-plan",
          }}
        />
      )}
    </>
  );
};
